import {useEffect} from "react";
import {useNavigate, useParams} from 'react-router-dom';
import {STATUS_MODEL} from "../../../../../enums/statusEnum";
import routes from "../../../../../Routes/routes";
import {invitationIsBlocked, invitationIsExpired} from "../Model/Model";


/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @JobTitle: Full Stack Developer
 *
 * Custom hook that validates invitation status and restricts access
 * by redirecting to the forbidden page when unauthorized states are detected.
 *
 * Validation logic:
 * - Forbidden if `visitorStatusId`, `identityValidation`, or `statusId`
 *   are in `[REJECTED, CANCELED, DENIED]`.
 * - Forbidden if:
 *    - documentationFlow = true → only when `statusId === APPROVED && visitorStatusId === ACCEPTED`
 *    - documentationFlow = false → when `identityValidation === VALIDATED && visitorStatusId === ACCEPTED`
 * - Forbidden if `statusId === CANCELED`.
 *
 * @function useForbiddenValidation
 * @param {Object} params - Hook parameters.
 * @param {Object} params.getInvitationById - Redux state or API response containing invitation details.
 * @param {boolean} [params.documentationFlow=false] - Whether the check is in documentation flow.
 *
 * @returns {void}
 */



const useForbiddenValidation = ({ getInvitationById ,documentationFlow= false}) => {
    const navigate = useNavigate();
    const {eventId, buildingId} = useParams();

    useEffect(() => {
        if(eventId || buildingId) return;
        if (!getInvitationById?.invitation) return;

        invitationIsBlocked(getInvitationById?.invitation, navigate);
        invitationIsExpired(getInvitationById?.invitation, navigate);

        const visitorStatusId = getInvitationById.invitation.visitorStatusId;
        const statusId = getInvitationById.invitation.statusId;
        const identityValidation = getInvitationById.invitation.identityValidation;

        const forbiddenStatuses = [
            STATUS_MODEL.REJECTED,
            STATUS_MODEL.CANCELED,
            STATUS_MODEL.DENIED,
        ];

        const isSpecialConditionMet =
            (documentationFlow
                ? statusId === STATUS_MODEL.APPROVED
                : identityValidation === STATUS_MODEL.VALIDATED) &&
            visitorStatusId === STATUS_MODEL.ACCEPTED;

        const isForbidden =
            forbiddenStatuses.includes(visitorStatusId)
            || forbiddenStatuses.includes(identityValidation)
            || forbiddenStatuses.includes(statusId)
            || isSpecialConditionMet;

        if (isForbidden) {
            navigate(routes.forbidden);
        }
    }, [getInvitationById, navigate]);
};

export default useForbiddenValidation
